<script setup lang="ts">
import type { HTMLAttributes } from "vue";
import { computed } from "vue";
import { cn } from "@/lib/utils";
import { glassVariants } from "./index";

const props = withDefaults(
    defineProps<{
        class?: HTMLAttributes["class"];
        // variants
        blur?: "none" | "xs" | "sm" | "md" | "lg" | "xl";
        tint?: "none" | "light" | "dark" | "primary" | "accent";
        elevation?: "none" | "sm" | "md" | "lg";
        border?: "none" | "muted" | "strong";
        interactive?: boolean;
        padding?: "none" | "sm" | "md" | "lg";
        rounded?: "none" | "sm" | "md" | "lg" | "full";
        /**
         * Additional classes for the blur effect
         */
        effectClass?: HTMLAttributes["class"];
        /**
         * Additional classes for the slot parent container (content wrapper)
         */
        contentClass?: HTMLAttributes["class"];
        /**
         * Additional attributes to bind on the slot parent container
         */
        contentAttrs?: HTMLAttributes;
    }>(),
    {
        class: "",
        blur: "md",
        tint: "none",
        elevation: "md",
        border: "muted",
        interactive: false,
        padding: "none",
        rounded: "md",
        effectClass: "",
        contentClass: "",
        contentAttrs: undefined,
    },
);

const classes = computed(() =>
    cn(
        glassVariants({
            blur: props.blur,
            tint: props.tint,
            elevation: props.elevation,
            border: props.border,
            interactive: props.interactive,
            padding: props.padding,
            rounded: props.rounded,
        }),
        // subtle sheen overlay relies on child element below
        "overflow-hidden",
        props.class,
    ),
);
</script>

<template>
    <div data-slot="glass" :class="classes">
        <!-- Sheen overlay for Apple-like glass effect -->
        <div
            aria-hidden="true"
            class="absolute inset-0 rounded-[inherit] pointer-events-none"
            :class="
                cn(
                    // diagonal soft-light sheen
                    'bg-linear-to-br from-white/10 via-transparent to-transparent',
                    // subtle grain using backdrop + ring already applied in base
                    'opacity-60',
                    // custom classes from props
                    effectClass,
                )
            "
        />

        <!-- Content -->
        <div class="relative flex" :class="props.contentClass" v-bind="props.contentAttrs">
            <slot />
        </div>
    </div>
</template>
